import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { connectDatabaseEmulator, getDatabase } from 'firebase/database';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import {
  connectFirestoreEmulator as connectRestFirestoreEmulator, getFirestore as getRestFirestore,
} from 'firebase/firestore/lite';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { firebaseProjectId } from './consoleEnvironment';
import { authHandlerDomain } from './consoleOrigin';

/**
 * The cloud project comes from the build environment, with NO default.
 *
 * A missing `VITE_FIREBASE_PROJECT_ID` used to fall back to `nuance-venue-dev`, which means a
 * production build with a broken environment would quietly sign people in to the dev project and drive
 * dev controllers. Now an incomplete environment is a hard, visible failure: `firebaseConfigured` is
 * false, the console renders its "not set up" state, and nothing is initialised against a guessed
 * project. The Pi-served bundle is the same build, so it carries the same environment.
 */
const projectId = firebaseProjectId;
const apiKey = import.meta.env.VITE_FIREBASE_API_KEY ?? '';

export const firebaseConfigured = projectId.length > 0 && apiKey.length > 0;
export const firebaseRegion = import.meta.env.VITE_FIREBASE_REGION ?? 'us-central1';

if (!firebaseConfigured) {
  console.error(
    '[nuance-venue] The cloud environment is incomplete: '
    + `${projectId ? '' : 'VITE_FIREBASE_PROJECT_ID '}${apiKey ? '' : 'VITE_FIREBASE_API_KEY '}`
    + 'is missing, so no cloud connection will be attempted.',
  );
}

const databaseURL = import.meta.env.VITE_FIREBASE_DATABASE_URL
  ?? (projectId ? `https://${projectId}-default-rtdb.firebaseio.com` : '');

export const firebaseApp = initializeApp({
  // An unconfigured build gets a name no project can answer to, so a mistake fails loudly at the first
  // request instead of reaching somebody else's project.
  projectId: projectId || 'configuration-required',
  apiKey: apiKey || 'configuration-required',
  authDomain: projectId ? authHandlerDomain(window.location, projectId) : 'configuration-required',
  databaseURL: databaseURL || 'https://configuration-required.firebaseio.com',
});

export const auth = getAuth(firebaseApp);
void setPersistence(auth, browserLocalPersistence);
export const functions = getFunctions(firebaseApp, firebaseRegion);
export const firestore = getFirestore(firebaseApp);
/**
 * The same app's REST-only Firestore (`firebase/firestore/lite`): the live view reads the controller's answer
 * through it (`OutputViewPeer.readAnswer`; the store hands this instance to the peer), because the listen
 * connection can be held server-side while one-off reads flow (CONTRACT-console H49). Exported from here, the
 * one module that points it at the emulator in emulator mode, so no caller depends on import order.
 */
export const restFirestore = getRestFirestore(firebaseApp);
export const realtime = getDatabase(firebaseApp);

// The browser journey runs against its own demo project and lane ports.
const emulatorOffset = import.meta.env.VITE_FIREBASE_EMULATOR_OFFSET;
if (emulatorOffset !== undefined) {
  if (!projectId.startsWith('demo-') || !/^\d+$/.test(emulatorOffset)) {
    throw new Error('Firebase emulator mode requires a demo project and numeric offset.');
  }
  const offset = Number(emulatorOffset);
  connectAuthEmulator(auth, `http://127.0.0.1:${9099 + offset}`, { disableWarnings: true });
  connectFirestoreEmulator(firestore, '127.0.0.1', 8088 + offset);
  connectRestFirestoreEmulator(restFirestore, '127.0.0.1', 8088 + offset);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001 + offset);
  connectDatabaseEmulator(realtime, '127.0.0.1', 9000 + offset);
}
